import React from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useLanguageStore } from '../../stores/languageStore';
import { postApi, categoryApi } from '../../utils/api';
import { Post, Category } from '../../types';
import { formatDistanceToNow } from 'date-fns';
import { zhCN, enUS } from 'date-fns/locale';
import toast from 'react-hot-toast';

const AdminPosts: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore();
  const { language } = useLanguageStore();
  const [posts, setPosts] = React.useState<Post[]>([]);
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [postToDelete, setPostToDelete] = React.useState<number | null>(null);
  const [showMoveModal, setShowMoveModal] = React.useState(false);
  const [postToMove, setPostToMove] = React.useState<Post | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (user?.role !== '管理员' && user?.role !== '站长') {
      return;
    }
    fetchPosts();
    fetchCategories();
  }, [page, user]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await postApi.getAdminPosts({ page, pageSize: 20 });
      if (response.success && response.data) {
        setPosts(response.data.posts || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryApi.getCategories();
      if (response.success && response.data) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  // 显示删除确认对话框
  const handleDeleteClick = (postId: number) => {
    setPostToDelete(postId);
    setShowDeleteModal(true);
  };

  // 显示移动帖子对话框
  const handleMoveClick = (post: Post) => {
    setPostToMove(post);
    setSelectedCategoryId(post.category_id);
    setShowMoveModal(true);
  };

  // 执行移动帖子
  const confirmMove = async () => {
    if (!postToMove || !selectedCategoryId) return;

    console.log('=== 前端移动帖子调试 ===');
    console.log('postToMove:', postToMove);
    console.log('postToMove.category_id:', postToMove.category_id);
    console.log('selectedCategoryId:', selectedCategoryId);
    console.log('发送的数据:', { category_id: selectedCategoryId });

    try {
      const response = await postApi.updatePost(postToMove.id, { category_id: selectedCategoryId });
      console.log('后端响应:', response);
      if (response.success) {
        toast.success('移动帖子成功');
        setShowMoveModal(false);
        setPostToMove(null);
        fetchPosts();
      }
    } catch (error: any) {
      console.error('移动失败:', error);
      toast.error(error.response?.data?.message || '移动失败');
    }
  };

  // 执行删除操作
  const confirmDelete = async () => {
    if (postToDelete === null) return;
    
    try {
      const response = await postApi.deletePost(postToDelete);
      if (response.success) {
        toast.success('删除成功');
        fetchPosts();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '删除失败');
    } finally {
      setShowDeleteModal(false);
      setPostToDelete(null);
    }
  };

  // 取消删除操作
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setPostToDelete(null);
  };

  const handleToggleTop = async (post: Post) => {
    // is_top 是枚举：'none', 'category', 'global'
    // 循环切换：none -> global -> category -> none
    let newTopStatus: 'none' | 'category' | 'global';
    if (post.is_top === 'none' || !post.is_top) {
      newTopStatus = 'global';
    } else if (post.is_top === 'global') {
      newTopStatus = 'category';
    } else {
      newTopStatus = 'none';
    }

    try {
      const response = await postApi.updatePost(post.id, { is_top: newTopStatus });
      if (response.success) {
        const statusText = newTopStatus === 'none' ? '取消置顶成功' : newTopStatus === 'global' ? '全站置顶成功' : '板块置顶成功';
        toast.success(statusText);
        // 如果设置为置顶，跳转到第一页查看（因为置顶帖子会排到最前面）
        if (newTopStatus !== 'none') {
          setPage(1);
        } else {
          fetchPosts();
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '操作失败');
    }
  };

  const getTopStatusText = (isTop: string | undefined) => {
    if (!isTop || isTop === 'none') return '置顶';
    if (isTop === 'global') return '全站置顶';
    if (isTop === 'category') return '板块置顶';
    return '置顶';
  };

  const handleToggleEssence = async (post: Post) => {
    try {
      const response = await postApi.updatePost(post.id, { is_essence: !post.is_essence });
      if (response.success) {
        toast.success(post.is_essence ? '取消精华成功' : '设为精华成功');
        fetchPosts();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '操作失败');
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: language === 'zh' ? zhCN : enUS });
    } catch {
      return dateString;
    }
  };

  if (!isAuthenticated || (user?.role !== '管理员' && user?.role !== '站长')) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">{'帖子管理'}</h1>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {'标题'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {'作者'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {'版块'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {'统计'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {'状态'}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {'操作'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {posts.map((post) => (
                <tr key={post.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <Link
                      to={`/post/${post.id}`}
                      className="text-sm font-medium text-gray-900 hover:text-primary-600 line-clamp-1"
                    >
                      {post.is_top === 'global' && <span className="text-red-500 mr-1">{'【全站置顶】'}</span>}
                      {post.is_top === 'category' && <span className="text-orange-500 mr-1">{'【板块置顶】'}</span>}
                      {post.is_essence && <span className="text-yellow-500 mr-1">{'【精华】'}</span>}
                      {post.title}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {post.author_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {post.category_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center gap-3">
                      <span>{'浏览'} {post.views}</span>
                      <span>{'回复'} {post.replies_count}</span>
                      <span>{'点赞'} {post.likes}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`badge ${
                        post.status === 'normal' ? 'badge-success' : 'badge-danger'
                      }`}
                    >
                      {post.status === 'normal' ? ('正常') : ('已删除')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleMoveClick(post)}
                      className="text-blue-600 hover:text-blue-900"
                      title={'移动帖子到其他版块'}
                    >
                      {'移动'}
                    </button>
                    <button
                      onClick={() => handleToggleTop(post)}
                      className={`${
                        post.is_top === 'global' ? 'text-red-600' :
                        post.is_top === 'category' ? 'text-orange-600' :
                        'text-gray-600'
                      } hover:text-red-900`}
                      title={
                        post.is_top === 'global' ? ('当前：全站置顶 → 点击切换为板块置顶') :
                        post.is_top === 'category' ? ('当前：板块置顶 → 点击取消置顶') :
                        ('当前：未置顶 → 点击全站置顶')
                      }
                    >
                      {getTopStatusText(post.is_top)}
                    </button>
                    <button
                      onClick={() => handleToggleEssence(post)}
                      className={`${
                        post.is_essence ? 'text-yellow-600' : 'text-gray-600'
                      } hover:text-yellow-900`}
                    >
                      {post.is_essence ? ('取消精华') : ('精华')}
                    </button>
                    <button
                      onClick={() => handleDeleteClick(post.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      {'删除'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 分页 */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 p-4 border-t border-gray-200">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn btn-secondary text-sm disabled:opacity-50"
            >
              {'上一页'}
            </button>
            <span className="text-sm text-gray-600">
              {'第'} {page} / {totalPages} {'页'}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="btn btn-secondary text-sm disabled:opacity-50"
            >
              {'下一页'}
            </button>
          </div>
        )}
      </div>

      {/* 删除确认模态框 */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-bold mb-4">{'确认删除'}</h2>
            <p className="mb-4">{'确定要删除该帖子吗？此操作不可撤销。'}</p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
              >
                {'取消'}
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
              >
                {'确认删除'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 移动帖子模态框 */}
      {showMoveModal && postToMove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{'移动帖子'}</h2>
            <p className="mb-2 text-sm text-gray-600">
              {'帖子'}: {postToMove.title}
            </p>
            <p className="mb-4 text-sm text-gray-600">
              {'当前版块'}: {postToMove.category_name}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {'选择目标版块'}
              </label>
              <select
                value={selectedCategoryId || ''}
                onChange={(e) => setSelectedCategoryId(parseInt(e.target.value) || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">{'请选择版块'}</option>
                {categories.map((cat) => (
                  <React.Fragment key={cat.id}>
                    <option value={cat.id}>
                      {cat.name}
                    </option>
                    {cat.children && cat.children.length > 0 && cat.children.map((child: Category) => (
                      <option key={child.id} value={child.id}>
                        {'　└ '}{child.name}
                      </option>
                    ))}
                  </React.Fragment>
                ))}
              </select>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowMoveModal(false);
                  setPostToMove(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
              >
                {'取消'}
              </button>
              <button
                onClick={confirmMove}
                disabled={!selectedCategoryId || selectedCategoryId === postToMove.category_id}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {'确认移动'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPosts;
